/*
    BoardUtils.js - Board utility routines
 */
import {State} from '@/paks/vu-state'
import {delay, expand, getModels, expression} from '@/paks/vu-app'
import {Progress} from '@/paks/vu-progress'
import {allow} from '@/paks/js-polyfill'
import Schema from '@/paks/mgr-data/HubSchema'
import UUID from '@/paks/js-uuid'
import Json5 from 'json5'
import Colors from './Colors'

const MimeTypes = {
    avif: 'image/avif',
    js: 'application/javascript',
    css: 'text/css',
    heif: 'image/heif',
    html: 'text/html',
    htm: 'text/html',
    ico: 'image/x-icon',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    json: 'application/json',
    map: 'application/json',
    mjs: 'application/javascript',
    pdf: 'application/pdf',
    png: 'image/png',
    txt: 'text/plain',
    webp: 'image/webp',
    xml: 'application/xml',
}

const VariableFields = [
    'accept',
    'axes',
    'datetime',
    'fields',
    'form',
    'format',
    'items',
    'legend',
    'limit',
    'max',
    'min',
    'multiple',
    'pivot',
    'prefix',
    'presentation',
    'rows',
    'subtitle',
    'suffix',
    'timezone',
    'units',
    'url',
    'validate',
]

const WidgetFields = {
    audio: ['url'],
    button: ['prefix', 'suffix', 'text'],
    checkbox: ['form'],
    combo: ['form', 'items', 'multiple'],
    date: ['form', 'datetime', 'prefix', 'suffix', 'timezone'],
    event: ['limit', 'max'],
    file: ['form', 'accept', 'multiple'],
    form: ['form'],
    gauge: ['format', 'min', 'max'],
    graph: ['axes', 'min', 'max', 'presentation'],
    icon: ['text'],
    image: ['url'],
    label: ['text'],
    led: [],
    list: [],
    metric: ['fields', 'pivot', 'subtitle'],
    numeric: ['format', 'prefix', 'suffix'],
    password: ['form'],
    progress: ['min', 'max'],
    radio: ['form', 'items'],
    set: [],
    select: ['form', 'items', 'multiple'],
    shape: [],
    slider: ['form', 'max', 'min', 'prefix', 'suffix'],
    switch: ['form'],
    speedometer: ['format', 'min', 'max'],
    sign: [],
    table: ['fields', 'pivot', 'subtitle'],
    tabs: ['items'],
    text: ['form', 'prefix', 'suffix', 'validate'],
    textarea: ['form', 'rows', 'validate'],
    toolbar: [],
    video: ['url'],
}

/*
    Provision the manager display app
*/
export async function provision(options = {}) {
    const {Manager} = getModels()
    Progress.start('dialog', 'Provisioning App', {bar: true, duration: 60 * 1000})
    let manager = await Manager.get({id: options.id})
    let prior = manager.provisioned || new Date()

    manager = await Manager.provision({id: manager.id, preserve: options.preserve})

    if (options.wait) {
        let deadline = Date.now() + options.wait
        while (Date.now() < deadline) {
            if (manager.provisioned > prior) {
                // This will discard changes to local dashboards
                // await State.cache.update()
                break
            }
            if (manager.error) {
                throw new Error('Cannot provision')
            }
            await delay(2000)
            manager = await Manager.get({id: manager.id}, {refresh: true, throw: false})
        }
        if (manager.provisioned == prior) {
            throw new Error('Timeout waiting for Provisioning')
        }
    }
    Progress.stop()
    return manager
}

export function getDisplay(name) {
    return State.app.fullDisplay.displays.find((d) => d.name == name)
}

/*
    Return {views, key, view} in the State.app.display.views
    NOTE: State.app.display must always be a direct reference to the fullDisplay.displays[item]
*/
export function getView(path, display = State.app.display) {
    let views = display.views || []
    for (let [key, view] of Object.entries(views)) {
        if (view.path == path) {
            return {parent: display, views: display.views, key, view}
        }
        if (view.views) {
            let found = getView(path, view)
            if (found.key >= 0) {
                return found
            }
            if (found.views) {
                return found
            }
        }
    }
    for (let view of views) {
        if (getViewDir(path) == view.path) {
            // Re-parent the view
            view.views = view.views || []
            return {parent: view, views: view.views, key: -1}
        }
    }
    return {}
}

export function getViewDir(path) {
    return path.substring(0, path.lastIndexOf('/')) || '/'
}

export function getViewChildren(views, result = []) {
    if (!views) {
        return result
    }
    for (let view of views) {
        result.push(view)
        if (view.views) {
            getViewChildren(view.views, result)
        }
    }
    return result
}

export function removeDisplay(display) {
    let full = State.app.fullDisplay
    let index = full.displays.findIndex((d) => d.name == display.name)
    if (index >= 0) {
        full.displays.splice(index, 1)
    }
    if (display.name == State.app.display.name) {
        display = State.app.pickDisplay(full)
        State.app.setDisplay(display)
    }
}

export function removeView(view) {
    let found = getView(view.path)
    if (found.key >= 0) {
        found.views.splice(found.key, 1)
    }
}

export function removeDashboard(dashboard) {
    let index = State.cache.dashboards.findIndex((d) => d.name == dashboard.name)
    if (index >= 0) {
        let item = State.cache.dashboards.splice(index, 1)
        State.cache.removedDashboards.push(item[0])
    }
}

export function updateDisplay(display) {
    let full = State.app.fullDisplay
    let index = full.displays.findIndex((d) => d.name == display.name)
    if (index >= 0) {
        full.displays[index] = display
    } else {
        full.displays.push(display)
    }
    if (display.name == State.app.display.name) {
        State.app.setDisplay(display)
    }
}

export function updateView(view) {
    let {views, key} = getView(view.path)
    if (key >= 0) {
        views[key] = view
    } else if (views) {
        views.push(view)
    } else {
        let display = State.app.display
        display.views[0].views.push(view)
    }
}

export function updateDashboard(dashboard) {
    // Update the board cache and set the need to save flag
    dashboard = cleanBoard(dashboard)
    State.cache.dashboard = dashboard
    let index = State.cache.dashboards.findIndex((d) => d.id == dashboard.id)
    if (index >= 0) {
        State.cache.dashboards.splice(index, 1, dashboard)
    } else {
        State.cache.dashboards.push(dashboard)
    }
}

export async function persistDisplay() {
    Progress.start('short')
    const {Manager} = getModels()
    let fullDisplay = cleanFullDisplay(State.app.fullDisplay)
    if (!fullDisplay?.displays || fullDisplay.displays.length == 0) {
        throw new Error('No displays to persist')
    }
    let manager = await Manager.updateDisplay({
        id: State.app.app.id,
        display: JSON.stringify(fullDisplay, null, 4),
    })
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    //  Ensure routes are updated
    State.app.setFullDisplay(fullDisplay)
    State.app.noNeed('deploy')
    Progress.stop()
    return manager
}

/*
    Upload the assets to the app
 */
export async function uploadAsset(manager, filename, data) {
    const {Manager} = getModels()
    Progress.start('dialog', `Upload ${filename}`, {bar: true, duration: 30 * 1000})
    Progress.message('Uploading', filename)
    await uploadFile(manager, filename, data)
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    Progress.stop()
}

export async function uploadZip(manager, list, preserve = []) {
    const {Manager} = getModels()
    Progress.start('dialog', 'Upload Assets', {bar: true, duration: 30 * 1000})
    try {
        for (let [filename, file] of Object.entries(list.files)) {
            if (file.dir || preserve.find((p) => filename.indexOf(p) == 0)) {
                continue
            }
            if (file.dir) continue
            Progress.message('Uploading', filename)
            let data = await file.async('blob')
            await uploadFile(manager, filename, data)
        }
    } catch (err) {
        throw err
    } finally {
        Progress.stop()
    }
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    return manager
}

async function uploadFile(manager, filename, data) {
    const {Manager} = getModels()
    let mimeType = getMimeType(filename)
    let url = await Manager.getSignedUrl({
        id: manager.id,
        filename,
        mimeType,
        command: 'put',
    })
    let response = await fetch(url, {
        method: 'PUT',
        body: data,
        headers: {'Content-Type': mimeType},
    })
    if (response.status != 200) {
        console.error(filename, response)
        throw new Error(`Cannot upload ${filename}`)
    }
}

function getMimeType(file) {
    let ext = file.match(/\.([0-9a-zA-Z]+)+$/i)
    if (!ext) {
        return 'application/octet-stream'
    }
    return MimeTypes[ext[1]]
}

function cleanCss(css) {
    if (css && typeof css == 'object') {
        if (!Array.isArray(css)) {
            //  REPAIR - very old object format
            let list = []
            for (let [name, value] of Object.entries(css)) {
                // Remove trailing semicolon
                value = value.trim().replace(/;$/, '')
                list.push({name, value})
            }
            css = list
        } else {
            //  REPAIR - old format did not have a property
            let list = []
            for (let item of css) {
                if (item.property) {
                    list.push(item)
                } else {
                    let {name, value} = item
                    if (item.name == null) continue
                    let [expr, path, property] = name.split(':')
                    if (property == null) {
                        if (path == null) {
                            path = expr
                            expr = null
                        }
                        property = path.split(/[.,#]/).at(-1)
                        path = path.replace(RegExp(property + '$'), '').replace(/\.$/, '')
                    }
                    // Remove trailing semicolon
                    value = value.trim().replace(/;$/, '')

                    // Expression selected value pair
                    if (expr && value.indexOf(':') >= 0) {
                        value = value.split(':')
                    }
                    let entry = {property, value}
                    if (expr) {
                        entry.expr = expr
                    }
                    if (path) {
                        entry.path = path
                    }
                    list.push(entry)
                }
            }
            css = list
        }
    }
    return css || []
}

export function cleanWidget(widget, onRead = false) {
    let type = widget.type
    widget.id = widget.id || UUID()
    if (type == 'button') {
        type = widget.type = 'input'
        widget.input = 'button'
    }
    //  Also expand meta widget properties (data) from display.json5
    if (widget.data) {
        for (let [key, value] of Object.entries(widget.data || {})) {
            if (key == 'filter') {
                key = 'dimensions'
            }
            widget[key] = value
            widget.namespace = widget.namespace || 'Database'
        }
        if (State.cache.clouds.length == 1) {
            widget.cloudId = State.cache.clouds[0].id
        }
        delete widget.data
    }
    // Set default values
    if (widget.action) {
        if (widget.action.type && widget.action.type != 'none') {
            widget.actions = [widget.action]
        }
        delete widget.action
    }
    if (widget.actions) {
        widget.actions = widget.actions.filter((a) => a.type && a.type != 'none')
        if (widget.actions.length == 0) {
            delete widget.actions
        }
    }
    for (let action of widget.actions || []) {
        delete action._select
        delete action['undefined']
        if (action.conditions) {
            action.conditions = action.conditions.filter(
                (c) => !(c.expression == 'true' && JSON.stringify(c.params) == '"{}"')
            )
            if (action.conditions.length == 0) {
                delete action.conditions
            }
        }
    }
    widget.dimensions = widget.dimensions || {}
    widget.range = widget.range || {}
    widget.anchor = widget.anchor || {}
    widget.css = cleanCss(widget.css || [])

    // Set to string
    widget.width = widget.width?.toString()
    widget.height = widget.height?.toString()
    widget.top = widget.top?.toString()
    widget.left = widget.left?.toString()

    // REPAIR
    if (Number(widget.width) < 0) {
        widget.width = '0.31'
    }

    // Set to numbers
    if (widget.min != null) {
        widget.min = widget.min ? widget.min - 0 : 0
    }
    if (widget.max != null) {
        widget.max = widget.max ? widget.max - 0 : 0
    }
    if (widget.z != null && typeof widget.z == 'string') {
        widget.z = +widget.z
    }
    if (widget.prefix == '') {
        delete widget.prefix
    }
    if (widget.suffix == '') {
        delete widget.suffix
    }
    if (!widget.cloudId && !widget.namespace) {
        delete widget.metric
        delete widget.model
        delete widget.field
        delete widget.dimensions
        delete widget.statistic
        delete widget.accumulate
    }
    //  Remove metric fields if not needed
    if (!widget.namespace || widget.namespace == 'None') {
        delete widget.namespace
        delete widget.metric
        delete widget.dimensions
        delete widget.statistic
        delete widget.accumulate
    }

    if (widget.model) {
        delete widget.metric
        delete widget.statistic
        delete widget.accumulate
    }

    //  Fixups
    if (!widget.range?.override) {
        widget.range = {}
    }
    if (widget.to) {
        widget.actions = [{type: 'link', target: widget.to}]
        delete widget.to
    }

    //  Remove run-time fields
    for (let key of Object.keys(widget)) {
        if (key.indexOf('_') == 0) {
            delete widget[key]
        }
    }
    delete widget.value

    // TEMP
    delete widget.appliedStyles
    delete widget.hide
    delete widget.reposition
    delete widget.removedCss

    //  Remove fields not in the widget type
    let fields = WidgetFields[type] || WidgetFields[widget.input] || []
    for (let key of VariableFields) {
        if (widget[key] != null && !fields.find((f) => f == key)) {
            console.log(`${type} widget cleaned field: ${key}`)
            delete widget[key]
        }
    }
    if (type == 'toolbar') {
        delete widget.namespace
        delete widget.metric
        delete widget.model
    }
    // Remove undefined fields (preserve nulls for write)
    if (onRead) {
        widget = Object.fromEntries(Object.entries(widget).filter(([_, v]) => v != null))
    } else {
        widget = Object.fromEntries(Object.entries(widget).filter(([_, v]) => v !== undefined))
    }

    //  Check all fields are part of the schema
    let cleaned = allow(widget, Object.keys(Schema.models.Dashboard.widgets.items.schema))

    let okeys = Object.keys(widget).sort()
    let ckeys = Object.keys(cleaned).sort()

    if (ckeys.length != okeys.length) {
        //  TODO: Remove this - changing widget types can always lead to this
        console.log(`${type} widget cleaned fields`, {
            cleaned: ckeys,
            widget: okeys,
        })
        for (let [index, key] of Object.entries(widget)) {
            if (ckeys[index] != key) {
                //  TODO: Remove this
                console.log(`${type} widget cleaned field: ${key} at index ${index} in cleaned`)
                break
            }
        }
    }
    return cleaned
}

export function cleanFullDisplay(fullDisplay, onRead = false) {
    for (let [index, display] of Object.entries(fullDisplay.displays)) {
        fullDisplay.displays[index] = cleanDisplay(display, onRead)
    }
    return fullDisplay
}

export function cleanDisplay(display, onRead = false) {
    for (let [index, view] of Object.entries(display.views || [])) {
        display.views[index] = cleanView(view, onRead)
    }
    return display
}

export function cleanView(view, onRead = false) {
    if (view.views) {
        for (let [index, subview] of Object.entries(view.views)) {
            view.views[index] = cleanView(subview, onRead)
        }
    }
    if (view.board) {
        view.board = cleanBoard(view.board, onRead)
    }
    if (view.properties) {
        if (typeof view.properties == 'string') {
            if (view.properties == '{}' || view.properties.match(/^"/)) {
                view.properties = {}
            } else {
                try {
                    view.properties = Json5.parse(view.properties)
                } catch (e) {
                    view.properties = {}
                }
            }
        }
    }
    return view
}

export function cleanBoard(board, onRead = false) {
    /*
        These fixes are needed to cleanup Hub stored board
    */
    board.widgets = board.widgets || []
    board.css = board.css || []
    board.range = board.range || {}
    board.widgetCss = cleanCss(board.widgetCss || [])
    board.css = cleanCss(board.css || [])
    board.type = board.type || 'dashboard'

    if (board.layout == null) {
        board.layout = 'grid'
    }
    //  Remove run-time fields
    for (let key of Object.keys(board)) {
        if (key.indexOf('_') == 0) {
            delete board[key]
        }
    }

    board.widgets.forEach((w) => {
        if (!w.id) {
            w.id = UUID()
        }
    })
    for (let [index, widget] of Object.entries(board.widgets)) {
        board.widgets[index] = cleanWidget(widget, onRead)
    }

    // Remove undefined fields (preserve nulls for write)
    if (onRead) {
        board = Object.fromEntries(Object.entries(board).filter(([_, v]) => v != null))
    } else {
        board = Object.fromEntries(Object.entries(board).filter(([_, v]) => v !== undefined))
    }

    let cleaned = allow(board, Object.keys(Schema.models.Dashboard))
    let okeys = Object.keys(board).sort()
    let ckeys = Object.keys(cleaned).sort()

    if (ckeys.length != okeys.length) {
        console.log(`Board cleaned fields`, {okeys, ckeys})
    }
    return cleaned
}

/*
    Expand the source URL via map then expand template ${vars}
    list: [{expression: '', value}, ...]
 */
export function pickItem(list, context) {
    let result
    if (list == null) {
        return {value: null}
    }
    if (list.indexOf('[') == 0) {
        list = Json5.parse(list)
        for (let item of list) {
            let enable = expression(item.expression || true, {context})
            if (enable) {
                result = item
                break
            }
        }
    } else if (list.indexOf('{') == 0) {
        let map = Json5.parse(list)
        result = {
            value: map[context.value] || map['*'],
        }
    } else {
        result = {value: list}
    }
    result.value = expand(result.value, context)
    return result
}

export function hex(x) {
    return ('0' + parseInt(x).toString(16)).slice(-2)
}

export function hex2rgb(color) {
    let parts = color.match(/^#?(?:(.)(.)(.)|(..)(..)(..)(..)?)$/)
    parts = parts.slice(4)
    return `rgba(${parseInt(parts[0], 16)}, ${parseInt(parts[1], 16)}, ${parseInt(parts[2], 16)}, ${parseInt(
        parts[3],
        16
    )})`
}

/* 
    Lighten a hex color by a percentage.
    @param {string} color The hex color string.
    @param {number} percent The percentage to lighten by.
    @returns {string} The lightened hex color string. 
*/
export function lighten(color, percent) {
    if (color[0] != '#') {
        return color
    }
    let opacity = color.slice(7, 9)
    color = color.slice(0, 7)
    let num = parseInt(color.slice(1), 16)
    let amt = Math.round(2.55 * percent)
    let R = (num >> 16) + amt
    let B = ((num >> 8) & 0x00ff) + amt
    let G = (num & 0x0000ff) + amt
    let newColor =
        '#' +
        (
            0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
            (G < 255 ? (G < 1 ? 0 : G) : 255)
        )
            .toString(16)
            .slice(1)
    if (opacity) {
        newColor += opacity
    }
    // console.log(color, newColor)
    return newColor
}

export function getProp(page, property, defaultValue) {
    let el = page.elements.primary
    if (el) {
        return el.style[property] || page.styles.getPropertyValue(property) || defaultValue
    }
    return defaultValue
}

/*
    Get a color from --v-${property} or ${theme} in the CSS styles
    Apply options.opacity only to the default color
 */
export function getColor(page, property, defaultColor, options = {}) {
    let opacity
    let el = page.elements.primary
    let color
    if (el) {
        // Get first from the primary element
        color = el.style[property]
    }
    if (!color) {
        // Now try the default color property
        if (defaultColor?.startsWith('--v-')) {
            color = el.style[defaultColor]
            if (!color) {
                color = page.styles.getPropertyValue(defaultColor)
            }
            if (color) {
                // Apply options.opacity only to the default color
                opacity = Math.min((options.opacity || 1) * 256, 255)
            }
        } else {
            // Plain color
            color = defaultColor
        }
    }
    if (!color) {
        color = '#000'
    } else if (!color.startsWith('#')) {
        // Convert to hex
        color = color.replace(/(rgb|rgba)\(/, '').replace(/\)/, '')
        var matches = color.match(/^(\d+),\s*(\d+),\s*(\d+)/)
        if (!matches) {
            matches = color.match(/^(\d+),\s*(\d+),\s*(\d+),\s*(\d+)/)
        }
        if (matches) {
            color = '#' + hex(matches[1]) + hex(matches[2]) + hex(matches[3]) + (matches[4] ? hex(matches[4]) : '')
        } else {
            color = Colors[color] || color
        }
    }
    // Apply opacity if required
    let parts = color.match(/^#?(?:(.)(.)(.)|(..)(..)(..)(..)?)$/)
    if (parts) {
        parts = parts.slice(4)
        if (parts[3] == null) {
            if (opacity) {
                parts[3] = hex(opacity)
            } else {
                parts[3] = 'FF'
            }
        }
        color = parts.join('').toUpperCase()
    }
    return '#' + color
}

export function getWidgetElements(components) {
    let elements = {}
    for (let [key, component] of Object.entries(components)) {
        let el = component.$el || component.value?.$el || component.value || component
        elements[key] = el
        if (key == 'primary' && el?.closest) {
            elements.card = el.closest('.widget-card')
        }
    }
    return elements
}

/* 
    Get a formatted value to display for each element.
    For primary field, we use widget.value, (widget.text overrides)
 */
export function getWidgetData(widget, elements, options) {
    let values = {}
    for (let ename of Object.keys(elements)) {
        let el = elements[ename] || {style: {}}
        let value = widget[ename]
        if (!value && ename == 'primary') {
            value = options.data ?? widget.text ?? widget.value ?? widget.defaultValue ?? el.style['-w--content']

            if (value && !options.multipoint && Array.isArray(value.points)) {
                value = value.points?.at(-1)?.value || widget.defaultValue || 0
                if (!widget.max) {
                    widget.max = Math.pow(10, (Math.abs(value - 0.51).toFixed(0) + '').length)
                }
            }
        }
        values[ename] = value
    }
    return values
}

/*
    Get the element (Vuetify) arguments for the element

    Two prefixes are supported:
    --v-- Make available to vue component properties via the binded arguments (args)
    --w-- Make available to widgets via CSS vars only

    This looks for the --w- prefixed CSS properties, strips the --w- prefix and adds them to the args. The defaults are default values to pass to vue components (--v-).
 */
export function getElementArgs(el, defaults = {}) {
    if (el.$el) {
        el = el.$el
    }
    let args = {}
    for (let [key, value] of Object.entries(defaults)) {
        args[key] = value
    }
    if (el.style) {
        for (let key of Object.keys(el.style).filter((n) => n.match(/^--[vw]-/))) {
            let bareKey
            bareKey = key.replace(/--.-/, '')
            let value = el.style[key]
            if (value == 'true') value = true
            else if (value == 'false') value = false
            else if (Number(value) == value) value = Number(value)
            if (key.indexOf('--v-') == 0) {
                args[bareKey] = value
            }
        }
    }
    return args
}

/*
    Apply default "styles" if not already applied
*/
export function applyDefaultWidgetStyles(widget, elements, defaultStyles = {}) {
    for (let [component, css] of Object.entries(defaultStyles)) {
        for (let [prop, value] of Object.entries(css)) {
            let key = `.${component}.${prop}`
            let cvalue = widget._appliedStyles?.[key]
            if (cvalue == null) {
                let el = elements[component]
                if (el && el.style) {
                    if (prop == 'class') {
                        for (let c of value.split(' ')) {
                            el.classList.add(c)
                        }
                    } else {
                        el.style[prop] = value
                        if (el.style[prop] != value) {
                            console.log(`Could not set widget prop ${prop} to ${value}`)
                        }
                    }
                }
            }
        }
    }
    return getComputedStyle(elements.primary)
}

export function getWidgetProps(widget, components, options = {}) {
    let elements = getWidgetElements(components)
    let args = getElementArgs(elements.primary, options.args)
    let styles = applyDefaultWidgetStyles(widget, elements, options.styles)
    let data = getWidgetData(widget, elements, options)
    return {args, data, elements, styles}
}
